import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useDropzone} from 'react-dropzone';
import ControlPanel from './ControlPanel';
import DropZone from './DropZone';
import FilesListAsTable from './FilesListAsTable';
import DropFileHereMessage from './DropFileHereMessage';
import {IOrderProductFileModel} from '@src/@interfaces/models/Order/IOrderProductFileModel';
import app from '@src/helpers/app';
import {AsyncFileUploaderContext} from '@src/components/AsyncFileUploader/AsyncFileUploaderContext';
import {isEqual} from '@src/helpers/is-equal';
import {pushUploadingFile, removeUploadingFile} from '@src/_redux/order-product-files/actions';
import {Events as FormEvents} from '@src/components/_forms/FormViewForm';
import {Events} from '@src/@interfaces/services/Http/IAsyncUploader';
import {IReduxRootState} from '@src/@interfaces';
import usePermissionGranted from '@src/hooks/usePermissionGranted';

const emptyArray: never[] = [];

export default function ListOfFiles(props: {
    files: IOrderProductFileModel[],
    orderId?: string | number | null,
    productId?: string | number | null,
    productCode: string,
    uploaderContextControllerName?: string,
    disabled?: boolean,
    onRefresh?: () => void,
}) {
    const {push, getItem, listen} = useContext(AsyncFileUploaderContext);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [deletedFiles, setDeletedFiles] = useState<Record<string | number, number>>({});
    const uploadingFilesHashes = useSelector<IReduxRootState, string[]>(state => {
        return state.orderProductFiles.uploadingFiles[props.productCode] ?? emptyArray;
    });
    const dispatch = useDispatch();

    const canUploadFiles = usePermissionGranted('orders@can-upload-order-product-files');
    const shouldUploadImmediately = true;

    const [uploaderContext, onDrop, onDelete, onOpen] = useMemo(() => {
        const uploaderContext = {
            type: 'order-product-file',
            controllerName: props.uploaderContextControllerName ?? 'Orders',
            orderId: props.orderId,
            productId: props.productId,
            productCode: props.productCode,
        };

        const onDrop = (files: FileList | File[]) => {
            const getSign = (file: File): string => {
                return `${file.name}-${file.size}-${file.type}-${file.lastModified}`;
            };
            const getUniqueListFiles = (list: File[]): File[] => {
                const existFiles: Record<string, number> = {};

                return [
                    ...(files instanceof FileList ? Array.from(files).reverse() : files),
                    ...list,
                ].reduce<File[]>((res, item) => {
                    const sign = getSign(item);
                    if (!(sign in existFiles)) {
                        existFiles[sign] = res.push(item);
                    }

                    return res;
                }, []);
            };

            if (shouldUploadImmediately) {
                getUniqueListFiles([]).forEach(file => push(file, uploaderContext));

                return;
            }

            setAttachments(list => getUniqueListFiles(list));
        };

        const onDelete: React.ComponentProps<typeof FilesListAsTable>['onDelete'] = (file, i) => {
            if (file instanceof File) {
                setAttachments(list => {
                    const newList = [...list];
                    newList.splice(i, 1);

                    return [...newList];
                });

                return Promise.resolve(true);
            }

            return app('http').route('orders.products.files.destroy', {
                id: String(props.orderId),
                product: String(props.productId),
                file: String(file.id),
            }, {
                headers: {'content-type': 'application/json'},
                body: {'controller-name': 'Orders'}
            }).then(() => {
                setTimeout(() => {
                    setDeletedFiles(map => {
                        return {...map, [String(file.id)]: Date.now()};
                    });
                }, 10);

                return true;
            });
        };

        const onOpen: React.ComponentProps<typeof FilesListAsTable>['onOpen'] = async (file) => {
            window.open(file.file.asset_file_path, '_blank');
        };

        return [uploaderContext, onDrop, onDelete, onOpen];
    }, [props.orderId, props.productCode, props.productId, props.uploaderContextControllerName, push, shouldUploadImmediately]);

    useEffect(() => {
        if (!canUploadFiles || shouldUploadImmediately) {
            return;
        }

        const subscription = app('events').listen<{
            controllerName: string,
        }>(FormEvents.ON_SUBMIT_SUCCESS, (e, payload) => {
            if (payload.controllerName === uploaderContext.controllerName) {
                attachments.forEach(file => push(file, uploaderContext));
                setAttachments([]);
            }
        });

        return () => subscription.unsubscribe();
    }, [attachments, canUploadFiles, push, shouldUploadImmediately, uploaderContext]);

    useEffect(() => {
        const subscription = listen<{ uniqueId: string }>([Events.FILE_ADDED_TO_LIST], (event, {uniqueId}) => {
            const item = getItem(uniqueId);
            if (item && isEqual(item.getMeta().context, uploaderContext)) {
                dispatch(pushUploadingFile(uploaderContext.productCode, [uniqueId]));
            }
        });

        return () => subscription.unsubscribe();
    }, [dispatch, getItem, listen, uploaderContext]);

    useEffect(() => {
        const check = () => {
            const hashes = uploadingFilesHashes.filter(hash => !getItem(hash));
            if (hashes.length) {
                dispatch(removeUploadingFile(uploaderContext.productCode, hashes));
            }
        };

        const subscription = listen(Events.FILE_REMOVED_FROM_LIST, check);

        check();

        return () => subscription.unsubscribe();
    }, [dispatch, getItem, listen, uploaderContext.productCode, uploadingFilesHashes]);

    const dragState = useDropzone({
        onDrop: onDrop,
        noClick: true,
        noKeyboard: true,
        preventDropOnDocument: false,
        disabled: props.disabled || !canUploadFiles,
    });

    const filesToDisplay = props.files.filter(item => !(String(item.id) in deletedFiles));

    const [searchParams] = useSearchParams();
    const listRef = useRef<HTMLDivElement>(null);
    const [scrolledTo, setScrolledTo] = useState<string | null>(null);

    useEffect(() => {
        const itemId = searchParams.get('fileId');
        if (!itemId || itemId === scrolledTo || !listRef.current) {
            return;
        }

        const highlightedItem = listRef.current.querySelector('.order__files-list__row--highlighted');
        if (highlightedItem) {
            highlightedItem.scrollIntoView();

            setScrolledTo(itemId);
        }
    }, [filesToDisplay?.length, scrolledTo, searchParams]);

    return <DropZone
            className="pt-2 rounded border"
            {...dragState}
            plain
    >
        <ControlPanel
                controllerName="Orders"
                orderId={props.orderId}
                productCode={props.productCode}
                onChooseFileClick={dragState.open}
                onRefresh={props.onRefresh}
                disabled={props.disabled}
        />

        <div style={{maxHeight: '500px', overflowX: 'hidden', overflowY: 'auto'}} className="mt-2" ref={listRef}>
            {(filesToDisplay.length || attachments.length || uploadingFilesHashes.length) ?
                    <FilesListAsTable
                            uploadedFiles={filesToDisplay}
                            newFiles={attachments}
                            uploadingFilesHashes={uploadingFilesHashes}
                            onOpen={onOpen}
                            onDelete={onDelete}
                            uploaderContext={uploaderContext}
                            disabled={props.disabled}
                    /> :
                    <div className="px-2 pb-2">
                        <DropFileHereMessage onClick={dragState.open} disabled={!canUploadFiles || props.disabled}/>
                    </div>
            }
        </div>
    </DropZone>;
}
